import { createFileRoute } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/roof-inspections-for-realtors";
const NAME = "Roof Inspections for Realtors";
const TITLE = "Roof Inspections for Realtors in Phoenix | Durafoam Roofing";
const DESCRIPTION =
  "Pre-listing and pre-close roof inspections for Phoenix realtors. Detailed written reports, photos, and repair estimates — turnaround typically within 48 hours.";

const faqs = [
  {
    q: "How quickly can you turn around a transaction inspection?",
    a: "Most inspections are scheduled within 48 hours and the written report follows within 24 hours of the inspection. Rush turnarounds are available when escrows are tight — call us.",
  },
  {
    q: "Do you provide a certification or just a report?",
    a: "We provide a detailed written report with photos, conditions documented, estimated remaining life, and itemized recommended repairs. Some lenders or buyers ask for a 'roof certification' — we can structure the report to satisfy that requirement.",
  },
  {
    q: "What does an inspection cost?",
    a: "Inspections for realtors and homeowners are free. We're betting we earn future repair work by being thorough and honest now.",
  },
  {
    q: "Will you upsell my client on unnecessary work?",
    a: "No. We document conditions accurately and recommend only what's actually needed. A reputation for honest reports is more valuable to us than one inflated invoice.",
  },
  {
    q: "Can you provide repair estimates buyers and sellers can use to negotiate?",
    a: "Yes. Our reports include line-item repair estimates that hold up in negotiation. We're happy to do follow-up conversations with the buyer, seller, or agent.",
  },
];

export const Route = createFileRoute("/roof-inspections-for-realtors")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) => serviceHead({ override: loaderData?.seo, slug: SLUG, title: TITLE, description: DESCRIPTION, serviceName: NAME, faqs, rating: loaderData?.rating, reviewCount: loaderData?.reviewCount }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="roof-inspections-for-realtors"
      eyebrow="For Realtors"
      h1="Roof Inspections for Phoenix Realtors"
      subhead="Pre-listing and pre-close roof inspections you can hand a buyer with confidence — detailed, accurate, and turned around fast enough to keep escrows on track."
      heroBullets={[
        "48-hour scheduling typical",
        "Detailed report with photos",
        "Line-item repair estimates",
        "Free for clients and agents",
      ]}
      whatIs={{
        heading: "Inspections built for real estate transactions",
        body: (
          <>
            <p>
              A roof report inside a real estate transaction has a different job than a homeowner
              maintenance report. The buyer needs to know what they're inheriting, the seller
              needs to know what's negotiable, the agent needs documentation they can defend, and
              the lender or insurer may need a specific format.
            </p>
            <p>
              Our reports cover all of it: roof type, age estimate, condition by section, photos
              of every flagged item, remaining useful life, and a line-item repair list with
              estimated costs. Reports are written in plain language — no industry jargon that
              forces a follow-up call to interpret.
            </p>
            <p>
              We work with agents across the Valley on dozens of transactions a year. Many became
              repeat referrals after their first deal with us went smoothly.
            </p>
          </>
        ),
      }}
      climate={{
        heading: "What buyers should know about Phoenix roofs",
        body: (
          <>
            <p>
              Roofs in the Valley behave differently than roofs in milder climates. A 20-year-old
              tile roof in Seattle might still have 20 years left; a 20-year-old tile roof in
              Phoenix likely needs underlayment replacement. Knowing what's normal here keeps
              transactions from breaking down over avoidable misunderstandings.
            </p>
            <ul>
              <li>
                <strong>Tile roofs:</strong> Tile lasts but the underlayment underneath wears at
                20–25 years. Many listing roofs need underlayment replacement — that's standard
                Phoenix maintenance, not a defect.
              </li>
              <li>
                <strong>Foam roofs:</strong> Recoats every 5 years in Phoenix are part of normal
                ownership. A foam roof past its coating cycle isn't "bad" — it's overdue.
              </li>
              <li>
                <strong>Shingle roofs:</strong> Phoenix UV cuts shingle life. A 15-year shingle
                roof here is closer to end-of-life than the same roof in a mild climate.
              </li>
              <li>
                <strong>Pipe boots and sealants:</strong> Almost always due for refresh on any
                Valley roof over 7 years. Cheap to address; expensive to ignore.
              </li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our inspection process for realtors",
        steps: [
          {
            title: "Fast scheduling",
            body: "Call or email and we'll usually be on the roof within 48 hours. Rush windows available when escrow is tight.",
          },
          {
            title: "Thorough on-site inspection",
            body: "Full roof walk, attic-side check where accessible, and photography of every condition that matters.",
          },
          {
            title: "Written report within 24 hours",
            body: "Plain-language report with photos, age estimate, remaining life, and itemized repair recommendations with costs.",
          },
          {
            title: "Follow-up support",
            body: "We answer buyer, seller, and lender questions directly — keeps your transaction moving without back-and-forth through you.",
          },
        ],
      }}
      faqs={faqs}
      ctaTitle="Got a roof report you need fast?"
      ctaBody="Call us or fill out the form and we'll schedule the inspection — most reports back in your inbox within 72 hours of the call."
    />
  );
}
